import React, { useEffect, useState } from 'react';
import { ApiCollection } from '../../../config/envConfig';
import './styles.css';
import { useAxios } from '../../../hooks/useAxios';
import { FaSignOutAlt } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setLogoutUser } from '../../../redux/userSlice';
import { useOneSignalUser } from '../../../hooks/useOneSignalUser';


interface EducationalInfo {
  class: string;
  stream: string;
}

interface Organisation {
  id: string;
  organisation_name: string;
  organisation_address: string;
  organisation_contact_number: string | null;
}

interface UserProfile {
  id: string;
  email: string;
  user_role: string;
  mobile_number: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  educational_info: EducationalInfo;
  organisations: Organisation;
  subscription?: {
    is_subscription_active: boolean;
    start_date: string | null;
    expiry_date: string | null;
  };
}

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const axios = useAxios();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logoutUser } = useOneSignalUser();
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(ApiCollection.user.getProfileInfo);
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleLogout = async () => {
    try {
      // First logout from OneSignal
      await logoutUser();

      // Then proceed with app logout
      dispatch(setLogoutUser());
      navigate('/login');  
      localStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
      
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    // Clear any error/success messages when user starts typing
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await axios.post(ApiCollection.user.changePassword, {
        old_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      setPasswordError(error.response?.data?.message || 'Failed to change password');
    }
  };

  const togglePasswordForm = () => {
    setShowPasswordForm(!showPasswordForm);
    // Reset form and messages when toggling
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!profile) {
    return <div className="error">Failed to load profile</div>;
  }


  

  return (
    <div className="profile_container">
      <div className="profile_header">
        <h2>My Profile</h2>
        <div className="logout_section mobile_only">
          <button className="logout_button" onClick={handleLogout}>
            <FaSignOutAlt className="logout_icon" />
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      <div className="profile_content">
        <div className="profile_section">
          <h2>Personal Information</h2>
          <div className="info_grid">
            <div className="info_item">
              <label>Full Name</label>
              <input 
                type="text" 
                value={`${profile.first_name} ${profile.last_name}`} 
                disabled 
              />
            </div>
            <div className="info_item">
              <label>Email Address</label>
              <input 
                type="email" 
                value={profile.email} 
                disabled 
              />
            </div>
            <div className="info_item">
              <label>Mobile Number</label>
              <input 
                type="tel" 
                value={profile.mobile_number} 
                disabled 
              />
            </div>
            {profile.user_role !== 'student' && (
            <div className="info_item">
              <label>User Role</label>
              <input 
                type="text" 
                value={profile.user_role} 
                disabled 
              />
            </div>
            )}
          </div>
        </div>

        <div className="profile_section">
          <h2>Educational Information</h2>
          <div className="info_grid">
            <div className="info_item">
              <label>Class</label>
              <input 
                type="text" 
                value={profile.educational_info.class} 
                disabled 
              />
            </div>
            <div className="info_item">
              <label>Stream</label>
              <input 
                type="text" 
                value={profile.educational_info.stream} 
                disabled 
              />
            </div>
          </div>
        </div>
{/* 
        {profile.user_role !== 'student' && (
          <div className="profile_section organizations">
            <h2>Organizations</h2>
            <div className="organizations-grid">
              <div key={profile.organisations.id} className="organization-card">
                <h3>{profile.organisations.organisation_name}</h3>
                <div className="org-details">
                  <div className="org-info">
                    <label><b>Address</b></label>
                    <span>{profile.organisations.organisation_address}</span>
                  </div>
                  {profile.organisations.organisation_contact_number && (
                    <div className="org-info">
                      <label>Contact</label>
                      <span>{profile.organisations.organisation_contact_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* <div className="profile_section subscription">
          <h2>Subscription Details</h2>
          <div className="subscription-info">
            {profile.subscription ? (
              <>
                <div className="subscription-status">
                  <span className={`status-badge ${profile.subscription.is_subscription_active ? 'active' : 'inactive'}`}>
                    {profile.subscription.is_subscription_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {profile.subscription.start_date && (
                  <div className="subscription-dates">
                    <div className="date-info">
                      <label>Start Date</label>
                      <span>{new Date(profile.subscription.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="date-info">
                      <label>Expiry Date</label>
                      <span>{profile.subscription.expiry_date ? new Date(profile.subscription.expiry_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div>No subscription information available</div>
            )}
          </div>
        </div> */}

        <div className="profile_section password_section">
          <div className="password_header">
            <h2>Security Setting</h2>
            <button 
              type="button" 
              className="toggle_password_button"
              onClick={togglePasswordForm}
            >
              {showPasswordForm ? 'Cancel' : 'Change Password'}
            </button>
          </div>
          
          {showPasswordForm && (
            <form onSubmit={handlePasswordSubmit} className="password_form">
              <div className="info_grid">
                <div className="info_item">
                  <label>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="info_item">
                  <label>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="info_item">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
              </div>
              {passwordError && <div className="password_error">{passwordError}</div>}
              {passwordSuccess && <div className="password_success">{passwordSuccess}</div>}
              <button type="submit" className="password_submit_button">
                Update Password
              </button>
            </form>
          )}
        </div>

        <div className="logout_section desktop_only">
          <button className="logout_button" onClick={handleLogout}>
            <FaSignOutAlt className="logout_icon" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
